import { produce } from 'immer';
import { useReducer } from 'react';
import { IFileState } from './MultiFileUpload/MultiFileUpload';

interface MultiFileUploadState {
  fileStatesByName: Record<string, IFileState>;
}

enum MultiFileUploadActionType {
  UPDATE_FILE_STATE = 'UPDATE_FILE_STATE',
  REMOVE_FILE_STATE = 'REMOVE_FILE_STATE',
  MERGE_FILE_STATES = 'MERGE_FILE_STATES',
  SET_FILE_ERROR = 'SET_FILE_ERROR',
}

interface IUpdateFileStateAction {
  type: MultiFileUploadActionType.UPDATE_FILE_STATE;
  fileName: string;
  partialState: Partial<IFileState>;
}

interface IRemoveFileStateAction {
  type: MultiFileUploadActionType.REMOVE_FILE_STATE;
  fileName: string;
}

interface IMergeFileStatesAction {
  type: MultiFileUploadActionType.MERGE_FILE_STATES;
  newStates: Record<string, IFileState>;
}

interface ISetFileErrorAction {
  type: MultiFileUploadActionType.SET_FILE_ERROR;
  fileName: string;
  error: string;
}

type MultiFileUploadAction =
  | IUpdateFileStateAction
  | IRemoveFileStateAction
  | IMergeFileStatesAction
  | ISetFileErrorAction;

const multiFileUploadReducer = (
  state: MultiFileUploadState,
  action: MultiFileUploadAction
): MultiFileUploadState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case MultiFileUploadActionType.UPDATE_FILE_STATE: {
        const fileState = draft.fileStatesByName[action.fileName];
        if (!fileState) {
          draft.fileStatesByName[action.fileName] = {} as IFileState;
        }
        Object.assign(
          draft.fileStatesByName[action.fileName],
          action.partialState
        );
        break;
      }

      case MultiFileUploadActionType.REMOVE_FILE_STATE: {
        delete draft.fileStatesByName[action.fileName];
        break;
      }

      case MultiFileUploadActionType.MERGE_FILE_STATES:
        Object.assign(draft.fileStatesByName, action.newStates);
        break;

      case MultiFileUploadActionType.SET_FILE_ERROR: {
        let fileState = draft.fileStatesByName[action.fileName];
        if (!fileState) {
          draft.fileStatesByName[action.fileName] = {} as IFileState;
          fileState = draft.fileStatesByName[action.fileName];
        }
        fileState.status = 'error';
        fileState.error = action.error;
        break;
      }
    }
  });
};

export const useMultiFileUploadState = (): {
  fileStatesByName: Record<string, IFileState>;
  updateFileState: (
    fileName: string,
    partialState: Partial<IFileState>
  ) => void;
  removeFileState: (fileName: string) => void;
  mergeFileStates: (newStates: Record<string, IFileState>) => void;
  setFileError: (fileName: string, error: string) => void;
} => {
  const [state, dispatch] = useReducer(multiFileUploadReducer, {
    fileStatesByName: {},
  });

  const updateFileState = (
    fileName: string,
    partialState: Partial<IFileState>
  ) => {
    dispatch({
      type: MultiFileUploadActionType.UPDATE_FILE_STATE,
      fileName,
      partialState,
    });
  };

  const removeFileState = (fileName: string) => {
    dispatch({ type: MultiFileUploadActionType.REMOVE_FILE_STATE, fileName });
  };

  const mergeFileStates = (newStates: Record<string, IFileState>) => {
    dispatch({ type: MultiFileUploadActionType.MERGE_FILE_STATES, newStates });
  };

  const setFileError = (fileName: string, error: string) => {
    dispatch({
      type: MultiFileUploadActionType.SET_FILE_ERROR,
      fileName,
      error,
    });
  };

  return {
    fileStatesByName: state.fileStatesByName,
    updateFileState,
    removeFileState,
    mergeFileStates,
    setFileError,
  };
};
